import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type CSSProperties,
  type ForwardRefExoticComponent,
  type RefAttributes,
  createContext,
  forwardRef,
  useContext,
  useMemo,
} from 'react';
import type React from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/material';
import {
  type GridColDef,
  GridRow,
  type GridRowProps,
  type GridValidRowModel,
} from '@mui/x-data-grid';

export const REORDER_FIELD = '__reorder__';

type DragHandleContextValue = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
};

type SortableRowComponent = ForwardRefExoticComponent<
  GridRowProps & RefAttributes<HTMLDivElement>
>;

const RowDragHandleContext = createContext<DragHandleContextValue | null>(null);

const SortableRowInnerContext = createContext<SortableRowComponent>(
  GridRow as SortableRowComponent
);

function DragHandleCell() {
  const drag = useContext(RowDragHandleContext);
  if (!drag) {
    return null;
  }

  return (
    <Box
      {...drag.attributes}
      {...drag.listeners}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'grab',
        color: 'text.secondary',
        touchAction: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <DragIndicatorIcon fontSize="small" />
    </Box>
  );
}

export function createReorderColumn<
  R extends GridValidRowModel = GridValidRowModel,
>(): GridColDef<R> {
  return {
    field: REORDER_FIELD,
    headerName: '',
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    resizable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: () => <DragHandleCell />,
  };
}

const SortableDataGridRow = forwardRef<HTMLDivElement, GridRowProps>(
  function SortableDataGridRow(props, ref) {
    const InnerRow = useContext(SortableRowInnerContext);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: String(props.rowId) });

    const style: CSSProperties = {
      ...props.style,
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : props.style?.opacity,
      zIndex: isDragging ? 1 : props.style?.zIndex,
      position: props.style?.position ?? 'relative',
    };

    const setRefs = (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    return (
      <RowDragHandleContext.Provider value={{ attributes, listeners }}>
        <InnerRow ref={setRefs} {...props} style={style} />
      </RowDragHandleContext.Provider>
    );
  }
);

export type DataTableRowReorder = {
  onReorder: (orderedIds: string[]) => void;
};

type RowReorderProviderProps = {
  rowIds: string[];
  onReorder: (orderedIds: string[]) => void;
  innerRow: SortableRowComponent;
  children: React.ReactNode;
};

export function RowReorderProvider({
  rowIds,
  onReorder,
  innerRow,
  children,
}: RowReorderProviderProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    onReorder(arrayMove(rowIds, oldIndex, newIndex));
  };

  return (
    <SortableRowInnerContext.Provider value={innerRow}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      </DndContext>
    </SortableRowInnerContext.Provider>
  );
}

export function useRowReorderSlot(
  enabled: boolean,
  baseRowSlot: SortableRowComponent | undefined
) {
  return useMemo(() => {
    if (!enabled) {
      return baseRowSlot ? { row: baseRowSlot } : undefined;
    }
    return { row: SortableDataGridRow };
  }, [enabled, baseRowSlot]);
}

export function getSortableInnerRow(
  baseRowSlot: SortableRowComponent | undefined
): SortableRowComponent {
  return baseRowSlot ?? (GridRow as SortableRowComponent);
}
